"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { OnboardingState, OnboardingStep } from "@/hooks/useOnboarding";
import { TourStep } from "./TourStep";
import { useLocation } from "react-router-dom";

interface OnboardingTourProps extends OnboardingState {
    onNext: () => void;
    onPrev: () => void; // Added
    onSkip: () => void;
    onComplete: () => void;
}

interface StepConfig {
    title: string;
    content: string;
    selector: string;
    position: "top" | "bottom" | "left" | "right" | "center";
}

const STEPS: Record<Exclude<OnboardingStep, "ready" | "completed">, StepConfig> = {
    nickname: {
        title: "1. 고객 닉네임 입력",
        content: "가장 먼저 고객의 닉네임을 입력하거나\n'고객 닉네임'을 클릭해서 수정하세요.",
        selector: '[data-tour="nickname-input"]',
        position: "bottom"
    },
    addItem: {
        title: "2. 상품 추가",
        content: "판매한 상품을 입력하면 자동으로 '최근 추가한 상품'에 등록됩니다.\n다음부터는 클릭 한 번으로 간편하게 추가하세요!",
        selector: '[data-tour="product-list"]',
        position: "right"
    },
    settings: {
        title: "3. 배송/결제 설정",
        content: "배송비, 입금계좌, 무료배송 기준금액을\n셀러 분들에게 맞게 설정하세요.",
        selector: '[data-tour="settings-toggle"]',
        position: "bottom"
    },
    orderControls: {
        title: "4. 주문 관리 (보관/상태)",
        content: "주문을 '킵(보관)'하거나 '입금완료' 등으로 상태를 변경할 수 있습니다.",
        selector: '[data-tour="order-controls"]',
        position: "bottom"
    },
    checkBill: {
        title: "5. 정산서 확인 및 복사",
        content: "상품을 담으면 정산서가 자동으로 만들어집니다.\n복사 버튼을 눌러 카톡으로 바로 보내세요!",
        selector: '[data-tour="settlement-area"]',
        position: "top"
    }
};

export function OnboardingTour({
    isActive,
    currentStep,
    onNext,
    onPrev,
    onSkip,
    onComplete
}: OnboardingTourProps) {
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [windowWidth, setWindowWidth] = useState(0);
    const [windowHeight, setWindowHeight] = useState(0);
    const location = useLocation();

    // 리사이즈 핸들러
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
            setWindowHeight(window.innerHeight);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // 타겟 요소 찾기 및 업데이트
    useEffect(() => {
        if (!isActive || currentStep === "ready" || currentStep === "completed") {
            setTargetRect(null);
            return;
        }

        const config = STEPS[currentStep as keyof typeof STEPS];
        if (!config) return;

        const updateRect = () => {
            // Select all matching elements and find the first visible one
            const elements = document.querySelectorAll(config.selector);
            const element = Array.from(elements).find(el => {
                const rect = el.getBoundingClientRect();
                return rect.width > 0 && rect.height > 0;
            });

            if (element) {
                const rect = element.getBoundingClientRect();
                // 화면 밖으로 나갔는지 체크하여 스크롤
                if (
                    rect.top < 0 ||
                    rect.bottom > window.innerHeight ||
                    rect.left < 0 ||
                    rect.right > window.innerWidth
                ) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                // 스크롤 후 rect 다시 계산 필요할 수 있음을 고려하여 약간 지연 후 설정
                // 하지만 getBoundingClientRect는 실시간이므로 requestAnimationFrame 사용
                setTargetRect(rect);
            } else {
                // 요소가 아직 없을 수 있음 (특히 상품 추가 같은 경우)
                // 재시도 로직이나 폴백
                console.warn(`[Onboarding] Target element not found: ${config.selector}`);
            }
        };

        updateRect();

        // DOM 변경 관찰 (요소가 나중에 생기는 경우 대비)
        const observer = new MutationObserver(updateRect);
        observer.observe(document.body, { childList: true, subtree: true });

        // 약간의 지연 후 한 번 더 체크 (애니메이션 등 고려)
        const timer = setTimeout(updateRect, 300);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
        };
    }, [isActive, currentStep, windowWidth, windowHeight]);

    // 포탈 렌더링 방지
    if (!isActive || currentStep === "ready" || currentStep === "completed" || !targetRect) {
        return null;
    }

    // 현재 페이지가 /order가 아니면 렌더링 안함 (안전장치)
    if (location.pathname !== '/order') return null;

    const config = STEPS[currentStep as keyof typeof STEPS];
    const stepIndex = Object.keys(STEPS).indexOf(currentStep);
    const totalSteps = Object.keys(STEPS).length;

    // 툴팁 위치 계산 로직
    const getTooltipStyle = () => {
        const gap = 12; // 화살표 공간
        const tooltipWidth = 320;
        // const tooltipHeight = ... (동적이라 알기 어려움, 대략적인 위치 잡기)

        let top = 0;
        let left = 0;

        switch (config.position) {
            case "bottom":
                top = targetRect.bottom + gap;
                left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                break;
            case "top":
                top = targetRect.top - gap; // 높이 고려 필요 (transform: translateY(-100%) 사용)
                left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
                break;
            case "left":
                top = targetRect.top + (targetRect.height / 2); // translateY(-50%)
                left = targetRect.left - tooltipWidth - gap;
                break;
            case "right":
                top = targetRect.top + (targetRect.height / 2); // translateY(-50%)
                left = targetRect.right + gap;
                break;
        }

        // 화면 경계 처리 (간단하게)
        if (left < 10) left = 10;
        if (left + tooltipWidth > windowWidth - 10) left = windowWidth - tooltipWidth - 10;

        const style: React.CSSProperties = {
            position: "fixed",
            top: top,
            left: left,
        };

        if (config.position === 'top') style.transform = 'translateY(-100%)';
        if (config.position === 'left' || config.position === 'right') style.transform = 'translateY(-50%)';

        return style;
    };

    return createPortal(
        <div className="fixed inset-0 z-[9990] pointer-events-none">
            {/* 딤드 배경 (구멍 뚫기 - clip-path 사용 또는 box-shadow 트릭) */}
            {/* box-shadow 트릭 사용: 타겟 요소를 감싸는 div에 거대한 shadow를 줌 */}
            <div
                className="absolute transition-all duration-300 ease-in-out pointer-events-auto"
                style={{
                    top: targetRect.top - 4,
                    left: targetRect.left - 4,
                    width: targetRect.width + 8,
                    height: targetRect.height + 8,
                    borderRadius: 8,
                    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.6)",
                    zIndex: 9991
                }}
            />

            {/* 툴팁 컴포넌트 */}
            <div className="pointer-events-auto relative z-[9999]">
                <TourStep
                    stepIndex={stepIndex}
                    totalSteps={totalSteps}
                    title={config.title}
                    content={config.content}
                    onNext={stepIndex < totalSteps - 1 ? onNext : undefined}
                    onPrev={stepIndex > 0 ? onPrev : undefined}
                    onComplete={stepIndex === totalSteps - 1 ? onComplete : undefined}
                    onSkip={onSkip}
                    style={getTooltipStyle()}
                    position={config.position}
                />
            </div>
        </div>,
        document.body
    );
}
